<template>
  <div class="evidence-summary-card">
    <!-- Collapsed header -->
    <div class="evidence-header" @click="expanded = !expanded">
      <i :class="['bi', expanded ? 'bi-chevron-down' : 'bi-chevron-right', 'expand-chevron']" />
      <span class="evidence-label">EVIDENCE</span>
      <span class="evidence-summary flex-grow-1">{{ summaryText }}</span>
      <StatusBadge v-if="pendingCount > 0" variant="primary" compact pill>
        <span class="pulse-dot" /> Capturing...
      </StatusBadge>
    </div>

    <!-- Expanded listing detail -->
    <BCollapse :show="expanded" disable-auto-scroll-on-show>
      <div class="evidence-listing-list">
        <!-- Active listings -->
        <div v-if="activeListings.length > 0" class="listing-group">
          <div class="listing-group-label">Active listings</div>
          <ListingRow
            v-for="listing in activeListings"
            :key="listing.id"
            :listing="listing"
            :city-id="cityId"
            show-menu
            @retry="onRetry(listing)"
            @upload="openUpload(listing)"
            @mark-inactive="confirmInactive(listing)"
          />
        </div>

        <!-- Inactive listings -->
        <div v-if="inactiveListings.length > 0" class="listing-group">
          <div class="listing-group-label">Inactive listings</div>
          <ListingRow
            v-for="listing in inactiveListings"
            :key="listing.id"
            :listing="listing"
            :city-id="cityId"
          />
        </div>

        <!-- No listings -->
        <div v-if="listings.length === 0" class="no-listings text-muted small">
          No listings found for this property.
        </div>
      </div>
    </BCollapse>

    <!-- Mark inactive confirmation modal -->
    <ModalView
      :model-value="Boolean(inactiveListing)"
      @update:model-value="v => { if (!v) inactiveListing = null; }"
      title="Mark listing inactive"
      ok-title="Mark inactive"
      ok-variant="danger"
      :disable-ok="!inactiveNote"
      @ok="onMarkInactive"
    >
      <p>
        This listing will be marked as inactive and will no longer be subject to enforcement.
      </p>
      <div v-if="inactiveListing">
        <div class="small text-muted mb-2">
          {{ inactiveListing.url?.replace(/^https?:\/\//, '') || inactiveListing.id }}
        </div>
        <label class="form-label">Note <span class="text-muted">(required)</span></label>
        <BTextarea small v-model="inactiveNote" placeholder="e.g. Listing was removed by the host" />
      </div>
    </ModalView>

    <!-- Upload modal -->
    <ModalView
      v-model="showUploadModal"
      title="Upload Screenshot"
      hide-footer
    >
      <div v-if="uploadListing" class="d-flex flex-column gap-3">
        <a
          v-if="uploadListing.url"
          :href="safeHref(uploadListing.url)"
          target="_blank"
          class="btn btn-outline-dark d-flex align-items-center justify-content-center gap-2"
         rel="noopener noreferrer">
          <i class="bi bi-box-arrow-up-right" />
          Visit listing to take a screenshot
        </a>
        <EvidenceUploadZone
          :city-id="cityId"
          :listing-id="uploadListing.id"
          @uploaded="onUploadComplete"
        />
      </div>
    </ModalView>
  </div>
</template>

<script>
import { safeHref } from '@/safeRendering';
import StatusBadge from '@/components/common/StatusBadge.vue';
import BCollapse from '@/components/common/BCollapse.vue';
import ListingRow from '@/components/letters/ListingRow.vue';
import ModalView from '@/components/common/ModalView.vue';
import BTextarea from '@/components/common/BTextarea.vue';
import EvidenceUploadZone from '@/components/letters/EvidenceUploadZone.vue';
import { postApi } from '@/api';

export default {
  components: { StatusBadge, BCollapse, ListingRow, ModalView, BTextarea, EvidenceUploadZone },
  props: {
    listings: { type: Array, default: () => [] },
    cityId: { type: String, required: true }
  },
  emits: ['listings-changed'],
  data() {
    return {
      expanded: false,
      inactiveListing: null,
      inactiveNote: '',
      uploadListing: null,
      showUploadModal: false
    };
  },
  computed: {
    activeListings() {
      return this.listings.filter(l => l.status !== 'inactive');
    },
    inactiveListings() {
      return this.listings.filter(l => l.status === 'inactive');
    },
    capturedCount() {
      return this.activeListings.filter(l =>
        l.evidence?.status === 'captured' || l.evidence?.status === 'manual_upload'
      ).length;
    },
    pendingCount() {
      return this.activeListings.filter(l => l.evidence?.status === 'pending').length;
    },
    summaryText() {
      const active = this.activeListings.length;
      if (active === 0) return 'No active listings';
      const captured = this.capturedCount;
      if (captured === active){
        if (active === 1) {
          return '1 listing has a screenshot';
        } else {
          return `All ${active} listings have screenshots`;
        }
      } else {
        return `${captured} of ${active} active listing${active === 1 ? ' has' : 's have'} screenshots`;
      }
    }
  },
  watch: {
    inactiveListing() {
      this.inactiveNote = '';
    }
  },
  methods: {
    safeHref,

    async onRetry(listing) {
      try {
        await postApi(`/v1/cities/${this.cityId}/listings/${listing.id}/evidence/retry`);
        this.$emit('listings-changed');
      } catch (err) {
        console.error('Evidence retry failed:', err);
      }
    },
    openUpload(listing) {
      this.uploadListing = listing;
      this.showUploadModal = true;
    },
    onUploadComplete() {
      this.showUploadModal = false;
      this.uploadListing = null;
      this.$emit('listings-changed');
    },
    confirmInactive(listing) {
      this.inactiveListing = listing;
    },
    async onMarkInactive() {
      if (!this.inactiveListing || !this.inactiveNote) return;
      try {
        await postApi(`/v1/cities/${this.cityId}/listings/${this.inactiveListing.id}/status`, {
          status: 'inactive',
          note: this.inactiveNote
        });
        this.inactiveListing = null;
        this.$emit('listings-changed');
      } catch (err) {
        console.error('Mark inactive failed:', err);
      }
    }
  }
};
</script>

<style scoped>
.evidence-summary-card {
  border: 1px solid var(--bs-border-color);
  border-radius: 7px;
  background: var(--bs-light);
  margin-bottom: 16px;
}
.evidence-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}
.evidence-header:hover {
  background: rgba(0, 0, 0, 0.02);
}
.evidence-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color);
}
.evidence-summary {
  font-size: 0.8rem;
  color: var(--bs-body-color);
}

/* Listing list */
.evidence-listing-list {
  border-top: 1px solid var(--bs-border-color);
  padding: 8px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.listing-group-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bs-secondary-color);
  margin-top: 4px;
  margin-bottom: 4px;
}
</style>
